/**
 * Capsule-vs-voxel-field kinematic solver — move, then push out of the surfaces you overlap.
 *
 * Generic; no player feel in here (that lives in playerStepper). This is a faithful port of the
 * ORIGINAL mesh-BVH resolver, with "triangle" replaced by "voxel cell surface piece". The
 * original ran, per nearby triangle:
 *
 *     distance = tri.closestPointToSegment(axis, triPoint, capsulePoint)
 *     if (distance < radius) {
 *       depth = radius - distance
 *       direction = normalize(capsulePoint - triPoint)
 *       if (direction.y > 0.5) direction = +Y      // ground contacts push straight up
 *       axis.start += direction * depth            // in place: the next triangle sees this
 *       axis.end   += direction * depth
 *     }
 *
 * and that — plus sub-stepping — was the entire collision system. `surfacePieces.ts` explains why
 * enumerating surfaces, rather than sampling along directions (vertical columns, horizontal
 * whiskers, roll rays), is what makes stepping and slopes feel smooth instead of gritty: a
 * piece's contribution is a CONTINUOUS function of the pose, where a direction probe's is a step
 * function at every edge.
 *
 * Structure:
 *
 *  moveCapsule — subdivides the requested displacement to at most MAX_SUBSTEP_DISP (R/2) and per
 *    sub-move applies it, then resolves overlap. NOTHING is swept. The anti-tunnel guarantee is
 *    the sub-move cap itself: for a surface to end up unnoticed the capsule must pass more than
 *    2R + 2·SKIN (0.53 m) beyond it in ONE sub-move, against a cap of 0.125 m — so a floor or
 *    wall of any thickness is caught at any speed, terminal velocity included. (The original's
 *    real tunnelling bug was that an unloaded chunk had no collider at all, so there was nothing
 *    to be caught by; the unknown-space freeze in playerStepper is the honest fix for that, and
 *    it stays.)
 *
 *  resolveContacts — the loop above over every mixed cell overlapping the capsule, repeated
 *    until the largest push falls under WALL_EPS. Grounded / ceiling / wall state is read off the
 *    same contact set in a final classification pass, so the engine has no probes and no support
 *    search at all. Push-out cannot pull DOWN, which is why walking off a ledge is a clean
 *    gravity arc and why the deleted downhill snap cannot come back by construction.
 *
 *  tryStepUp — raise / advance / drop, all three through resolveContacts. Push-out alone cannot
 *    mount a VERTICAL riser: its face pushes purely sideways, and its top edge is out of the foot
 *    sphere's reach (0.354 m away for a 0.5 m riser, against a 0.25 m radius). Without this a
 *    0.5 m ledge is an impassable wall. This is the bounded, explicit version of the traversal
 *    the original got — unboundedly — from its +Y snap.
 *
 *  verifyCapsule / depenetrate — a 17-point interior check backstops everything; on failure the
 *    bounded ladder runs: resolveContacts with a longer budget, then an exact vertical escape
 *    scan for a capsule fully buried by an edit (no mixed cell, hence no piece, hence nothing for
 *    the resolver to push against). Restore/freeze on exhaustion is the caller's job.
 */
import { COLLISION_SKIN, ESCAPE_SCAN_UP, MAX_DEPEN_ITERS, MAX_SUBSTEP_DISP, MAX_SUBSTEPS, MAX_WALL_ITERS, MIN_STEP_NORMAL_Y, STEP_HEIGHT, SUPPORT_SLACK, } from '../protocol/physics.js';
import { VOXEL_SCALE } from '../voxel/constants.js';
import { INV_VOXEL_SCALE } from './voxelField.js';
import { collectPieces, makePieceSet } from './surfacePieces.js';
/** Gauss-Seidel convergence threshold (m): below this a residual push is not worth a pass. */
const WALL_EPS = 0.002;
/** Below this, a requested displacement is not worth a sub-move (m). */
const MOVE_EPS = 1e-6;
/**
 * Forward distances tried by the step-up query, as fractions of its reach (shortest first).
 *
 * A step necessarily surrenders the stand-off the capsule was holding, so it moves the player
 * further than the tick asked for — 0.265 m against a 0.05 m walking tick. Granularity is
 * therefore a SPEED dial, not a performance one: at 3 probes a 0.5 m riser was traversed 1.14x
 * faster than flat ground, which turns a staircase into a shortcut (R34-STEP-COST).
 */
const STEP_PROBE_STEPS = 3;
/**
 * Minimum contact-normal Y for the surface a STEP lands on — deliberately far above
 * MIN_STEP_NORMAL_Y (0.75 ≈ 41°, versus the 60° standing limit).
 *
 * A maximum-grade hillside reads exactly AT the standing limit, so a query using that
 * threshold is decided by 4-bit quantization noise and will happily walk the player up a 60°
 * slope one 0.5 m "step" at a time (R27). The wide margin is what makes "is this a tread or a
 * hillside?" a robust question: a real tread reads ≈ 1.0.
 */
const STEP_LANDING_NORMAL_Y = 0.75;
/**
 * Margin added to the capsule AABB when collecting pieces (m). It must cover the largest push
 * the resolver can apply within one call, so a cell that becomes relevant mid-solve is already
 * in the set: penetration is bounded by one sub-move (MAX_SUBSTEP_DISP = 0.125), and this is
 * comfortably above it. It must also exceed COLLISION_SKIN, which is what guarantees a cell
 * ENTERS the window with zero contribution and therefore cannot step the result.
 */
const PIECE_MARGIN = 0.15;
/**
 * The original's ground/wall split: a contact pushing more than 60° above horizontal is ground,
 * and its push is snapped to pure +Y. Identical in value to MIN_STEP_NORMAL_Y, which is not a
 * coincidence — it is the same question ("is this floor?") asked of the same geometry.
 */
const GROUND_DIR_Y = MIN_STEP_NORMAL_Y;
/** Heights used by the interior verifier, as fractions of innerHeight above the foot centre. */
const RING_T = [0.182, 0.591, 1.0];
// Module-level scratch (engine is single-threaded, non-reentrant by design)
const pieces = makePieceSet();
/**
 * Move the capsule by (dispX, dispY, dispZ) with collision. Mutates `io` in place.
 */
export function moveCapsule(field, hull, io, dispX, dispY, dispZ) {
    const preX = io.x;
    const preZ = io.z;
    const wasGrounded = io.grounded;
    moveAndResolve(field, hull, io, dispX, dispY, dispZ, MAX_WALL_ITERS);
    const want = dispX * dispX + dispZ * dispZ;
    // Concave-wall tangential refund. On a CURVED (voxel-faceted) wall the contact patch is deeper
    // on the side the capsule is moving toward, so the per-piece radial push-outs sum to a net
    // BACKWARD tangential component. A flat wall loses nothing — every push is exactly
    // perpendicular, which is why the R33 retention table never saw this — but a dug room's wrap
    // refunded most of each tick's slide and made small rooms sticky (R57: 6.7% travel in a 5 m
    // room against a 44% smooth-cylinder ideal). The collide-and-slide answer: re-walk the lost
    // horizontal motion ONCE, projected off the wall normals so none of it presses back into the
    // wall. In a corner wedge the remainder projects off both walls to ~zero, so this cannot
    // re-introduce the R56 buzz; on flat walls the remainder is already ~zero and this is a no-op.
    if (want > 0 && io.wallContact) {
        let remX = dispX - (io.x - preX);
        let remZ = dispZ - (io.z - preZ);
        for (let w = 0; w < wallCount; w++) {
            const into = remX * wallNx[w] + remZ * wallNz[w];
            if (into < 0) {
                remX -= into * wallNx[w];
                remZ -= into * wallNz[w];
            }
        }
        let rm = remX * remX + remZ * remZ;
        if (rm > want) {
            // Never re-walk more than the tick commanded (a depenetration burst is not motion owed).
            const k = Math.sqrt(want / rm);
            remX *= k;
            remZ *= k;
            rm = want;
        }
        if (rm > MOVE_EPS * MOVE_EPS) {
            moveAndResolve(field, hull, io, remX, 0, remZ, MAX_WALL_ITERS);
        }
    }
    // Push-out cannot see over a VERTICAL riser (see tryStepUp). If a walking move was impeded by
    // a contact, give the explicit step query a chance before accepting the obstacle as a wall. A
    // slope too steep to climb also lands here, and is turned away by the step query's own landing
    // gate.
    if (wasGrounded && io.grounded && io.wallContact && want > 0) {
        const progress = (io.x - preX) * dispX + (io.z - preZ) * dispZ;
        if (progress < want * 0.9)
            tryStepUp(field, hull, io, dispX, dispZ);
    }
}
/**
 * Apply a displacement in sub-moves no larger than MAX_SUBSTEP_DISP, resolving contacts after
 * each. This IS the collision pipeline; everything else in this file either calls it or backs
 * it up.
 */
function moveAndResolve(field, hull, io, dispX, dispY, dispZ, iters) {
    const hMag = Math.sqrt(dispX * dispX + dispZ * dispZ);
    const maxDisp = Math.max(hMag, Math.abs(dispY));
    let n = maxDisp > MAX_SUBSTEP_DISP ? Math.ceil(maxDisp / MAX_SUBSTEP_DISP) : 1;
    if (n > MAX_SUBSTEPS)
        n = MAX_SUBSTEPS;
    const inv = 1 / n;
    const sx = dispX * inv;
    const sy = dispY * inv;
    const sz = dispZ * inv;
    for (let i = 0; i < n; i++) {
        io.x += sx;
        io.y += sy;
        io.z += sz;
        resolveContacts(field, hull, io, iters);
    }
}
/**
 * The contact loop. Every mixed cell overlapping the capsule contributes a closest-point push,
 * applied in place so the next piece sees the partly-resolved pose (Gauss-Seidel), repeated
 * until convergence. Contact flags and the ground normal come from a final pass that pushes
 * nothing, so they describe the pose the caller actually ends up with.
 */
function resolveContacts(field, hull, io, iters) {
    const R = hull.radius;
    const reach = R + COLLISION_SKIN;
    const M = R + PIECE_MARGIN;
    const fY0 = io.y - hull.innerHeight;
    collectPieces(field, io.x - M, fY0 - M, io.z - M, io.x + M, io.y + M, io.z + M, pieces);
    const count = pieces.count;
    if (count === 0) {
        io.grounded = false;
        io.groundNormalY = 1;
        io.groundNx = 1;
        io.groundNz = 0;
        wallCount = 0;
        return;
    }
    for (let iter = 0; iter < iters; iter++) {
        let worst = 0;
        for (let p = 0; p < count; p++) {
            const d = contactAt(io, hull, p);
            if (d >= reach)
                continue;
            let depth = reach - d;
            if (depth > R)
                depth = R; // saturated reading — never teleport
            // The original's ground snap: a contact pushing more than 60° above horizontal becomes a
            // pure vertical lift at full depth. This is what makes ground smooth — every ground-ish
            // contact pushes the SAME way, so which cell happens to be nearest stops being a decision,
            // and rest height becomes a continuous scalar instead of an argmax over directions. Climb
            // RATE is bounded by the caller's along-ground speed law (`stepPlayer`), which is where
            // that belongs.
            //
            // Its mirror image is new, and it is what the original was missing: everything that is NOT
            // ground pushes purely SIDEWAYS (or, for a ceiling, purely down). A wall must never lift
            // the capsule. Measured with walls pushing radially, the shoulder at the foot of a riser
            // reads dirY = 0.471 — a hair under the ground threshold — and its 17 mm push carried
            // enough lift to raise the capsule 58 mm, off the floor it was standing on and out of the
            // support window entirely. The result was a 34 mm hover-bounce with the player reading
            // airborne on most ticks (R28), and the same lift cost tangential speed all along a wall
            // (R33, R56, R57).
            //
            // Every push is therefore purely vertical or purely horizontal, which is the same
            // decomposition the whisker/column engine used — but the CONTACTS still come from
            // surfaces, which is the part that was wrong before, not this.
            if (cDirY > GROUND_DIR_Y) {
                io.y += depth;
            }
            else if (cDirY < -GROUND_DIR_Y) {
                io.y -= depth;
            }
            else {
                // Horizontal resolution of a perpendicular overlap: travelling `depth / hm` sideways
                // recovers `depth` of separation from a plane whose normal is `hm` horizontal. Grazing
                // contacts are skipped — the ratio explodes there and the geometry says nothing useful.
                const hm = Math.sqrt(cDirX * cDirX + cDirZ * cDirZ);
                if (hm < 0.2)
                    continue;
                let h = depth / hm;
                if (h > R)
                    h = R;
                io.x += (cDirX / hm) * h;
                io.z += (cDirZ / hm) * h;
                if (h > worst)
                    worst = h;
                continue;
            }
            if (depth > worst)
                worst = depth;
        }
        if (worst <= WALL_EPS)
            break;
    }
    // ---- Classification pass: no pushes, so this describes the final pose ----
    //
    // "Touching" is `d <= reach + WALL_EPS`, not `d < reach`: the loop above pushes every real
    // contact out to EXACTLY reach, so a strict test reports nothing and the pose reads as free of
    // walls and ceilings. That is not a tolerance, it is the definition of resting contact under
    // this solver. Ground gets the wider SUPPORT_SLACK window, which is the documented "how far
    // below the feet still counts as support" rule.
    const touch = reach + WALL_EPS;
    const support = reach + SUPPORT_SLACK;
    let grounded = false;
    // Ground normal is the proximity-weighted MEAN of the supporting pieces, not the nearest one.
    // A discretized slope is a staircase of cells whose individual gradients swing either side of
    // the true grade, and the nearest piece is biased toward whichever part sticks up closest —
    // measured, a 40° slope read as 49.7° and the caller's along-ground speed law then charged for
    // a climb that steep. Weighting by (support - d) smooths that the way the old two-sample
    // averaged normal did, but from real geometry, and it stays continuous as pieces enter and
    // leave (weight → 0 at the boundary).
    let gnx = 0;
    let gny = 0;
    let gnz = 0;
    let nWalls = 0;
    for (let p = 0; p < count; p++) {
        const d = contactAt(io, hull, p);
        if (d > touch || cDirY > GROUND_DIR_Y)
            continue;
        if (cDirY < -GROUND_DIR_Y) {
            io.hitCeiling = true;
            continue;
        }
        io.wallContact = true;
        // Only a NEAR-VERTICAL wall has a chamfer to disown (see isWallChamfer). A dug room's bowl is
        // steep without being vertical, and its wall genuinely does continue into the ground the
        // player is standing on — treating that junction as a chamfer flattened the reported grade and
        // made the room stickier, not less (R57).
        if (pieces.ny[p] > CHAMFER_WALL_NY)
            continue;
        const hm = Math.sqrt(pieces.nx[p] * pieces.nx[p] + pieces.nz[p] * pieces.nz[p]);
        if (hm > 1e-6 && nWalls < MAX_WALL_NORMALS) {
            wallNx[nWalls] = pieces.nx[p] / hm;
            wallNz[nWalls] = pieces.nz[p] / hm;
            nWalls++;
        }
    }
    for (let p = 0; p < count; p++) {
        const d = contactAt(io, hull, p);
        if (d >= support)
            continue;
        // GROUND is exactly what the push loop snapped to +Y — see isGroundContact. The two MUST
        // agree: a contact that lifts the capsule without grounding it leaves the player hovering and
        // then falling, tick after tick. Measured with the tests split, the capsule bounced 34 mm at
        // the foot of a 3-voxel riser and read airborne on most ticks (R28).
        //
        // The grade is the honest proximity-weighted mean over those pieces. It must never be faked
        // flat: a fallback of 1.0 told the step query that a 66° riser shoulder was a tread, so the
        // capsule "climbed" onto it and slid straight back off.
        if (!isGroundContact(p))
            continue;
        // GROUNDED means all but touching — see GROUND_TOUCH. Emphatically NOT the SUPPORT_SLACK
        // window used for the grade below: that was a probe-model idea ("how far below the feet still
        // counts as support") and it made sense when the vertical phase PLACED the capsule on
        // whatever it found. Push-out never pulls down, so the same window becomes a shelf the
        // capsule hovers on — measured in a pool, it stopped 52 mm above the bed, read grounded, had
        // its vy zeroed by the caller, and never fell the rest of the way.
        if (d <= reach + GROUND_TOUCH)
            grounded = true;
        // The GRADE still averages over the wider neighbourhood — that is what smooths a discretized
        // slope — but a chamfer never gets to claim the ground is steep.
        if (!isWallChamfer(p, nWalls)) {
            const w = support - d;
            gnx += pieces.nx[p] * w;
            gny += pieces.ny[p] * w;
            gnz += pieces.nz[p] * w;
        }
    }
    wallCount = nWalls;
    const gm = Math.sqrt(gnx * gnx + gny * gny + gnz * gnz);
    if (gm > 1e-9) {
        io.groundNormalY = gny / gm;
        // The normal points OUT of the solid, so its horizontal part already points downhill.
        io.groundNx = gnx / gm;
        io.groundNz = gnz / gm;
    }
    else {
        io.groundNormalY = 1;
        io.groundNx = 1;
        io.groundNz = 0;
    }
    if (grounded && !io.grounded)
        io.landed = true;
    io.grounded = grounded;
}
// Contact result for the piece last passed to contactAt (module scratch: this is the innermost
// loop in the engine and must not allocate).
let cDirX = 0;
let cDirY = 0;
let cDirZ = 0;
/**
 * Is the last contact GROUND — something to stand on and be lifted straight up by?
 *
 * The original asked only `direction.y > 0.5`, because it had no notion of a slope limit. Here
 * the surface must ALSO be walkable. Both halves earn their place:
 *
 *  - the direction test alone hoists the capsule up any steep face whose edge happens to point
 *    up-and-out, which is the unbounded ratchet this engine replaced with an explicit step query;
 *  - the walkability test alone misses a rounded shoulder that genuinely is being stood on.
 *
 * Steep contacts still push, just radially rather than vertically — which is what turns a cliff
 * into a slide and makes a climb cost forward speed instead of being a free lift.
 */
function isGroundContact(p) {
    return cDirY > GROUND_DIR_Y && pieces.ny[p] >= MIN_STEP_NORMAL_Y;
}
/** Horizontal normals of the wall contacts found this tick (module scratch). */
const MAX_WALL_NORMALS = 8;
const wallNx = new Float64Array(MAX_WALL_NORMALS);
const wallNz = new Float64Array(MAX_WALL_NORMALS);
/** How many of wallNx/wallNz are valid after the last resolveContacts (module scratch). */
let wallCount = 0;
/** Two horizontal normals this aligned belong to the same wall (≈ 45°). */
const CHAMFER_DOT = 0.7;
/** A wall must be this close to vertical (≈ 78°) before its base counts as a chamfer. */
const CHAMFER_WALL_NY = 0.2;
/**
 * How far off a surface still counts as STANDING on it (m). Squeezed from both sides:
 *
 *  - it must clear the resolver's convergence tolerance (WALL_EPS, 2 mm) plus a few ticks of
 *    gravity dip (2.8 mm each), or the capsule reads airborne between ticks and micro-hops over
 *    rough ground. Measured on seeded terrain, grounded ticks went 205 → 226 → 237 → 240+ at
 *    0 / 10 / 20 / 25 mm.
 *  - whatever it admits, the capsule can HOVER on, because push-out cannot pull down. That is why
 *    it is not SUPPORT_SLACK (60 mm), which is still the right window for averaging the GRADE but
 *    was a placement-model idea as a contact test: measured in a pool, the capsule stopped 52 mm
 *    above the bed, read grounded, had its vy zeroed by the caller, and never fell the rest of
 *    the way. Above 25 mm an inside corner starts to vibrate again (R56).
 */
const GROUND_TOUCH = 0.025;
/**
 * Is this walkable piece really the CHAMFER at the foot of a wall the capsule is also touching?
 *
 * SurfaceNets renders a vertical wall meeting a floor as a wall quad, a floor quad, and a bevel
 * between them — and that bevel reads as a perfectly walkable 45° surface. It is not ground: it
 * is the wall. Walking into a wall presses the foot sphere into the bevel and rides it up 107 mm,
 * which lifts the real floor out of the support window, leaving the bevel as the ONLY contact
 * setting the grade. The caller's along-ground speed law then charges the player for climbing a
 * 45° hill while they are merely sliding along a wall: measured, 83% tangential retention against
 * an 85% floor (R33), and the same cost showed up in corners and dug rooms (R56, R57).
 *
 * The bevel is identified by the thing that makes it a bevel: its horizontal normal points the
 * same way as the wall's. It still GROUNDS the capsule and still lifts it — the lift is real
 * geometry — it just does not get to claim the ground is steep.
 */
function isWallChamfer(p, nWalls) {
    if (nWalls === 0)
        return false;
    const nx = pieces.nx[p];
    const nz = pieces.nz[p];
    const hm = Math.sqrt(nx * nx + nz * nz);
    if (hm < 1e-6)
        return false; // dead flat: cannot belong to any wall
    const ux = nx / hm;
    const uz = nz / hm;
    for (let w = 0; w < nWalls; w++) {
        if (ux * wallNx[w] + uz * wallNz[w] > CHAMFER_DOT)
            return true;
    }
    return false;
}
/**
 * Distance from the capsule axis to piece `p`, writing the outward push direction into
 * (cDirX, cDirY, cDirZ). The bounded analogue of three-mesh-bvh's `closestPointToSegment`:
 *
 *  - the axis is vertical, so the closest point on it to the surface point is just the surface
 *    point's height clamped to the axis span — the general segment solution, for free;
 *  - the closest point on the PIECE is that axis point projected onto the piece's plane and then
 *    clamped to the cell box. A triangle is bounded, and this is what bounds a piece: without it
 *    a distant cell's plane extends across the whole world and phantom-contacts the capsule.
 *  - both steps are continuous (a box clamp is Lipschitz-1), which is what makes the resulting
 *    push a continuous function of the pose. That is the property the direction-probe models
 *    could not have.
 */
function contactAt(io, hull, p) {
    const fY = io.y - hull.innerHeight;
    const vx = pieces.vx[p];
    const vy = pieces.vy[p];
    const vz = pieces.vz[p];
    const nx = pieces.nx[p];
    const ny = pieces.ny[p];
    const nz = pieces.nz[p];
    const loX = pieces.loX[p];
    const loY = pieces.loY[p];
    const loZ = pieces.loZ[p];
    const hiX = pieces.hiX[p];
    const hiY = pieces.hiY[p];
    const hiZ = pieces.hiZ[p];
    // Closest axis point to the surface point, then refine against the closest piece point.
    let ay = vy < fY ? fY : vy > io.y ? io.y : vy;
    let qx = 0;
    let qy = 0;
    let qz = 0;
    for (let pass = 0; pass < 2; pass++) {
        const s = (io.x - vx) * nx + (ay - vy) * ny + (io.z - vz) * nz;
        qx = io.x - s * nx;
        qy = ay - s * ny;
        qz = io.z - s * nz;
        if (qx < loX)
            qx = loX;
        else if (qx > hiX)
            qx = hiX;
        if (qy < loY)
            qy = loY;
        else if (qy > hiY)
            qy = hiY;
        if (qz < loZ)
            qz = loZ;
        else if (qz > hiZ)
            qz = hiZ;
        if (pass === 1)
            break;
        const ay2 = qy < fY ? fY : qy > io.y ? io.y : qy;
        if (ay2 === ay)
            break;
        ay = ay2;
    }
    const dx = io.x - qx;
    const dy = ay - qy;
    const dz = io.z - qz;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // Push RADIALLY, away from the closest point — `capsulePoint - triPoint`, the original's own
    // direction. Deliberately NOT the signed distance to the piece's plane: a plane is infinite
    // and a piece is not, so every surface facing away from the capsule (the far side of a wall,
    // the underside of a floor) reports the axis as "behind" it from any distance. Measured, that
    // read pushed the capsule straight through a one-voxel wall — 0.92 m in a single tick, peak X
    // of 116 m against a 3.8 m budget (R23, R54).
    if (d < 1e-9) {
        // Degenerate: the axis passes exactly through the closest point. The original normalized a
        // zero vector here, got (0,0,0) from three.js, and silently applied no push at all — the
        // legacy stuck bug. The plane normal is the honest answer.
        cDirX = nx;
        cDirY = ny;
        cDirZ = nz;
        return 0;
    }
    const invD = 1 / d;
    cDirX = dx * invD;
    cDirY = dy * invD;
    cDirZ = dz * invD;
    // Behind the surface: the radial direction now points INTO the solid, so pushing along it
    // would drive the capsule deeper. Never do that — report no contact and let the interior
    // verifier's escape scan handle it, which is what that rung exists for. Reaching here needs
    // penetration past the axis itself (> R), and one sub-move is capped at R/2.
    if (dx * nx + dy * ny + dz * nz <= 0)
        return Infinity;
    return d;
}
/**
 * Explicit step-up query: raise, advance, drop.
 *
 * A stair riser or a built ledge is VERTICAL, so its surface pushes the capsule purely sideways
 * and its top edge sits out of the foot sphere's reach — for a 0.5 m riser the edge is 0.354 m
 * from the foot centre against a 0.25 m radius. Push-out therefore cannot mount it at any speed:
 * measured before this query existed, a 0.5 m ledge stopped the player dead from every approach
 * angle and a 0.5 m staircase advanced 0.20 m in five seconds.
 *
 * All three motions go through the ordinary pipeline, so a step lands wherever a walk would have
 * landed. It commits only when the capsule ends HIGHER than it started, no higher than
 * STEP_HEIGHT, grounded on a surface far flatter than the standing limit, with headroom, and
 * passing the interior verifier. Otherwise nothing moves and the obstacle stays a wall.
 *
 * The slope limit survives via the LANDING gate, not the trigger: a 60° hillside also cancels the
 * move and so also reaches this query, and is turned away because its surface reads far below
 * STEP_LANDING_NORMAL_Y (R27). A 3-voxel ledge is taller than the raised pose, so the raised pose
 * fails headroom or the drop fails to clear it (R28).
 */
function tryStepUp(field, hull, io, hdx, hdz) {
    const len = Math.sqrt(hdx * hdx + hdz * hdz);
    if (len <= MOVE_EPS)
        return false;
    const reach = hull.radius + COLLISION_SKIN;
    // Base on the pose the move ACTUALLY reached, not the pose it started from: an impeded move
    // usually still slid some distance along the obstacle, and reverting to the start would throw
    // that away every tick the player brushes a wall.
    const baseX = io.x;
    const baseY = io.y;
    const baseZ = io.z;
    const baseFy = baseY - hull.innerHeight;
    const savedGrounded = io.grounded;
    const savedNy = io.groundNormalY;
    const savedNx = io.groundNx;
    const savedNz = io.groundNz;
    // The raised pose must fit where we are standing.
    if (!headroomClear(field, hull, baseX, baseY + STEP_HEIGHT, baseZ))
        return false;
    // One attempt at a given forward distance: raise, advance, drop, all three through the ordinary
    // pipeline so a step lands exactly where a walk would have landed. Leaves `io` at the landing.
    const attempt = (advance) => {
        io.x = baseX;
        io.y = baseY + STEP_HEIGHT;
        io.z = baseZ;
        io.grounded = false;
        if (!verifyCapsule(field, hull, io.x, io.y, io.z))
            return false; // raised pose is inside solid
        moveAndResolve(field, hull, io, (hdx / len) * advance, 0, (hdz / len) * advance, MAX_WALL_ITERS);
        moveAndResolve(field, hull, io, 0, -STEP_HEIGHT, 0, MAX_WALL_ITERS);
        const newFy = io.y - hull.innerHeight;
        return (io.grounded &&
            newFy > baseFy + COLLISION_SKIN + 1e-9 &&
            newFy - baseFy <= STEP_HEIGHT + 1e-9 &&
            io.groundNormalY >= STEP_LANDING_NORMAL_Y &&
            headroomClear(field, hull, io.x, io.y, io.z) &&
            verifyCapsule(field, hull, io.x, io.y, io.z));
    };
    // Search the SHORTEST advance that lands. A step surrenders the whole stand-off the capsule was
    // holding — 0.265 m in a tick that asked for 0.05 — so every metre of it is free distance, and
    // a staircase built out of them out-runs flat ground. Coarse scan up (nothing shorter can work
    // once one fails), then bisect between the last failure and the first success to spend the
    // geometric minimum. Refining only AFTER a landing is found matters: widening the coarse scan
    // instead just gives a fast approach more chances to find a spurious landing, which is how a
    // finer scan alone put the capsule through a one-voxel wall (R54).
    let lo = 0;
    let landed = false;
    for (let c = 1; c <= STEP_PROBE_STEPS; c++) {
        const advance = (reach * c) / STEP_PROBE_STEPS;
        if (attempt(advance)) {
            let hi = advance;
            for (let i = 0; i < 4; i++) {
                const mid = (lo + hi) * 0.5;
                if (attempt(mid))
                    hi = mid;
                else
                    lo = mid;
            }
            if (!attempt(hi))
                break; // cannot happen: hi landed last time it was tried
            landed = true;
            break;
        }
        lo = advance;
    }
    if (landed) {
        io.landed = true;
        io.steppedUp = true;
        // Charge what the step actually gained horizontally (the landing may have clamped short of
        // the probed advance). The tick's own budget was already spent hitting the riser, so the
        // whole gain is beyond-budget distance for the stepper to repay as step-debt.
        io.stepAdvance += Math.hypot(io.x - baseX, io.z - baseZ);
        return true;
    }
    io.x = baseX;
    io.y = baseY;
    io.z = baseZ;
    io.grounded = savedGrounded;
    io.groundNormalY = savedNy;
    io.groundNx = savedNx;
    io.groundNz = savedNz;
    return false;
}
/** Quick headroom check for a candidate lifted pose (verifier backstops this). */
function headroomClear(field, hull, x, eyeY, z) {
    const R = hull.radius;
    if (field.sampleSolid(x, eyeY + R - COLLISION_SKIN, z) >= 0)
        return false;
    if (field.sampleSolid(x, eyeY + R * 0.5, z) >= 0)
        return false;
    if (field.sampleSolid(x, eyeY, z) >= 0)
        return false;
    return true;
}
// ============== Interior verifier + depenetration ladder ==============
/** Worst interior sample from the last verifyCapsule call (module scratch). */
export const verifyInfo = { worstPhi: -Infinity, x: 0, y: 0, z: 0 };
// 12 ring sample precomputes: 3 heights × 4 directions at 0.6R
const V_DIRX = [1, -1, 0, 0];
const V_DIRZ = [0, 0, 1, -1];
/**
 * Interior check: 17 samples of the capsule shrunk by 0.4R must all be air.
 * Full soundness is impossible cheaply (foot cells are always mixed-sign); misses are
 * limited to sub-sample quantization lobes a few cm across, which cannot trap the capsule.
 */
export function verifyCapsule(field, hull, x, y, z) {
    const R = hull.radius;
    const fY = y - hull.innerHeight;
    const lo = fY - R * 0.6;
    const hi = y + R * 0.6;
    verifyInfo.worstPhi = -Infinity;
    let ok = true;
    // 5 axis samples
    for (let k = 0; k < 5; k++) {
        const sy = lo + ((hi - lo) * k) / 4;
        const phi = field.sampleSolid(x, sy, z);
        if (phi >= 0) {
            ok = false;
            if (phi > verifyInfo.worstPhi) {
                verifyInfo.worstPhi = phi;
                verifyInfo.x = x;
                verifyInfo.y = sy;
                verifyInfo.z = z;
            }
        }
    }
    // Near-surface pole samples (2 cm inside the true caps) close the shrunk-interior blind
    // spot for spawns/teleports that overlap a floor or ceiling by less than 0.4·R. At rest
    // the bottom cap sits COLLISION_SKIN above the surface, so this still reads air.
    for (let k = 0; k < 2; k++) {
        const sy = k === 0 ? fY - R + 0.02 : y + R - 0.02;
        const phi = field.sampleSolid(x, sy, z);
        if (phi >= 0) {
            ok = false;
            if (phi > verifyInfo.worstPhi) {
                verifyInfo.worstPhi = phi;
                verifyInfo.x = x;
                verifyInfo.y = sy;
                verifyInfo.z = z;
            }
        }
    }
    // 3 rings × 4 at 0.6R
    for (let r = 0; r < 3; r++) {
        const sy = fY + hull.innerHeight * RING_T[r];
        for (let d = 0; d < 4; d++) {
            const sx = x + V_DIRX[d] * R * 0.6;
            const sz = z + V_DIRZ[d] * R * 0.6;
            const phi = field.sampleSolid(sx, sy, sz);
            if (phi >= 0) {
                ok = false;
                if (phi > verifyInfo.worstPhi) {
                    verifyInfo.worstPhi = phi;
                    verifyInfo.x = sx;
                    verifyInfo.y = sy;
                    verifyInfo.z = sz;
                }
            }
        }
    }
    return ok;
}
/**
 * Bounded depenetration ladder. Returns true when the capsule ends in verified air.
 * Rung 1: the contact resolver with a longer iteration budget — for shallow overlap from edits.
 * Rung 2: exact vertical escape scan, for BURIAL, which rung 1 structurally cannot fix: a
 *         capsule entirely inside solid has no mixed cell around it, so there is no surface
 *         piece to push against. (Matches the old "+Y pop" UX when terrain is added over the
 *         player.)
 * Restore-to-last-valid (rung 3) is the caller's job — it owns that state.
 */
export function depenetrate(field, hull, io) {
    resolveContacts(field, hull, io, MAX_DEPEN_ITERS);
    if (verifyCapsule(field, hull, io.x, io.y, io.z))
        return true;
    // Rung 2 — vertical escape scan: walk grid planes upward on the center column looking
    // for a solid→air boundary where the whole capsule verifies.
    const R = hull.radius;
    const feetY = io.y - hull.innerHeight - R;
    const k0 = Math.floor(feetY * INV_VOXEL_SCALE);
    const kMax = k0 + Math.ceil(ESCAPE_SCAN_UP * INV_VOXEL_SCALE);
    let wBelow = field.sampleSolid(io.x, k0 * VOXEL_SCALE, io.z);
    for (let k = k0 + 1; k <= kMax; k++) {
        const yPlane = k * VOXEL_SCALE;
        const wHere = field.sampleSolid(io.x, yPlane, io.z);
        if (wBelow >= 0 && wHere < 0) {
            // Solid below, air above: boundary in [k-1, k]; exact linear root
            const t = wBelow / (wBelow - wHere);
            const surfaceY = (k - 1 + t) * VOXEL_SCALE;
            const candEye = surfaceY + COLLISION_SKIN + R + hull.innerHeight;
            if (verifyCapsule(field, hull, io.x, candEye, io.z)) {
                io.y = candEye;
                io.grounded = true;
                io.landed = true;
                return true;
            }
        }
        wBelow = wHere;
    }
    return false;
}
//# sourceMappingURL=capsule.js.map